import {
  canAccessControlDataManagement,
  canAccessProductManagement,
  canAccessSalesManagement,
  canAccessSchoolManagement,
  canAccessServiceProviderManagement
} from 'Services/common/Features/User/userSlice'
import useTypedSelector from 'Store'
import { colors } from 'Util/colors'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  PRODUCT_MANAGEMENT_ROOT_URL,
  SALES_MANAGEMENT_ROOT_URL,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import * as React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { t } from 'ttag'

const StyledButton = styled(Button)(({ theme }) => ({
  color: colors.textPrimaryDark
}))

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.primary,
  display: 'block',
  padding: theme.spacing(0.75, 2)
}))

function NavigationMenu() {
  const { user } = useTypedSelector(state => state.user)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const location = useLocation()
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const serviceLinks = [
    {
      title: t`Sales management`,
      link: SALES_MANAGEMENT_ROOT_URL,
      requiredUserPermissions: [canAccessSalesManagement]
    },
    {
      title: t`Product management`,
      link: PRODUCT_MANAGEMENT_ROOT_URL,
      requiredUserPermissions: [canAccessProductManagement]
    },
    {
      title: t`Service provider management`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
      requiredUserPermissions: [canAccessServiceProviderManagement]
    },
    {
      title: t`Control data management`,
      link: CONTROL_DATA_MANAGEMENT_ROOT_URL,
      requiredUserPermissions: [canAccessControlDataManagement, canAccessSchoolManagement]
    }
  ]

  // Filter out service links that user is not allowed to see
  const allowedServiceLinks = serviceLinks.filter(({ requiredUserPermissions }) => {
    return requiredUserPermissions.some(permission => permission(user)) !== false
  })

  // Hide navigation dropdown if the user only has access to one service
  if (allowedServiceLinks.length < 2) {
    return null
  }

  return (
    <>
      <StyledButton
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {allowedServiceLinks.find(({ link }) => location.pathname.includes(link))?.title ??
          t`Sales management`}
      </StyledButton>
      <Menu
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {allowedServiceLinks.map(({ title, link }) => (
          <MenuItem
            key={title}
            onClick={handleClose}
            selected={location.pathname.includes(link)}
            disableRipple
            sx={{ p: 0 }}
          >
            <StyledNavLink to={{ pathname: link }}>{title}</StyledNavLink>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default NavigationMenu
